// src/widgets/components/ConversationWidget.tsx
import { Box, IconButton } from '@chakra-ui/react';

import { useEffect, useState } from 'react';
import { CoreAdminContext, CoreAdminUI, CustomRoutes } from 'ra-core';
import { dataProvider } from '@/services/data-provider';
import authProvider from '@/services/auth-provider';
import ConversationsPage from '@/pages/conversations/conversations-page';
import { ConversationsProvider } from '@/context/conversation-context';
import LoginPage from '@/pages/login/login-page';
import { WidgetConfig } from '../types/config';
import { FiX } from 'react-icons/fi';
import { OrgLocationProvider } from '@/context/organization-location-context';
import { Route, Navigate, useNavigate } from 'react-router-dom';
import { WhatsAppEventsProvider } from '@/context/whatsapp-events-context';
import OrganisationSelectionPage from '@/pages/organisations/organisation-selection-page';
import LocationSelectionPage from '@/pages/locations/location-selection-page';
import { WidgetModalProvider } from '@/features/widget/components/widget-modal-provider';
import { getEnv } from '../utils/env';
import { persistStorageService } from '@/features/auth/persist-storage';
import { queryClient } from '@/lib/react-query';
import { useAuthUser } from '@/features/auth/auth.hook';
import { authService } from '@/features/auth/auth.service';

// import env variables
const env = getEnv();

// Simple widget layout that allows the host element to control scrolling
const WidgetLayout = ({ children }: { children: React.ReactNode }) => (
  <Box width="100%" height="100%" overflow="visible" pr={10} pl={4}>
    {children}
  </Box>
);

function ConversationWidget({ config }: { config: WidgetConfig }) {
  const [isOpen, setIsOpen] = useState(!!config.autoOpen);
  const isEmbedded = !!config.elementId;

  // Listen for open/close events from the container
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const isFullPage = config.isFullPage === true;
  const container =
    document.getElementById('conversations-widget-root') ||
    (config.elementId ? document.getElementById(config.elementId) : null);

  const containerHeight = container?.clientHeight;
  const containerWidth = container?.clientWidth;

  useEffect(() => {
    // Clear any existing auth data first
    persistStorageService.batchRemovePersistItems({
      names: ['token', 'refresh_token', 'user'],
    });
    // Handle authentication token
    if (config.authToken && config.authToken !== 'test-token-123') {
      persistStorageService.setPersistItem({
        name: 'token',
        value: config.authToken,
      });
    } else if (config.access_token && config.refresh_token) {
      persistStorageService.setPersistItem({
        name: 'token',
        value: config.access_token,
      });
      persistStorageService.setPersistItem({
        name: 'refresh_token',
        value: config.refresh_token,
      });
      // Invalidate auth queries to force fresh data
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    } else {
      persistStorageService.batchRemovePersistItems({
        names: ['token', 'refresh_token'],
      });
    }

    // Handle organization and location IDs with proper structure
    if (config.organisation_id) {
      const selectedOrganisation = {
        id: config.organisation_id,
        name: 'Organization', // Default name as per requirement
      };
      persistStorageService.setPersistItem({
        name: 'selectedOrganisation',
        value: selectedOrganisation,
        stringify: true,
      });
    }

    if (config.location_id) {
      const selectedLocation = {
        id: config.location_id,
        name: 'Location', // Default name as per requirement
      };
      persistStorageService.setPersistItem({
        name: 'selectedLocation',
        value: selectedLocation,
        stringify: true,
      });
    }

    // Store user role for role-based views
    if (config.user_role) {
      persistStorageService.setPersistItem({
        name: 'widget_user_role',
        value: config.user_role.toLowerCase(),
      });
    }

    container?.addEventListener('widget:open', handleOpen);
    container?.addEventListener('widget:close', handleClose);

    if (config.elementId) {
      setIsOpen(true);
    }

    return () => {
      container?.removeEventListener('widget:open', handleOpen);
      container?.removeEventListener('widget:close', handleClose);
      persistStorageService.batchRemovePersistItems({
        names: [
          'token',
          'refresh_token',
          'user',
          'selectedOrganisation',
          'selectedLocation',
          'widget_user_role',
        ],
      });
    };
  }, [
    config.authToken,
    config.organisation_id,
    config.location_id,
    config.user_role,
    config.access_token,
    config.refresh_token,
    config.elementId,
  ]);

  // Prevent body scroll when widget is open on mobile
  useEffect(() => {
    // Only lock body scroll for modal mode (not embedded)
    if (!isEmbedded) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
        document.body.classList.add('conversations-widget-mobile-open');
      } else {
        document.body.style.overflow = '';
        document.body.classList.remove('conversations-widget-mobile-open');
      }

      return () => {
        document.body.style.overflow = '';
        document.body.classList.remove('conversations-widget-mobile-open');
      };
    }
  }, [isOpen, isEmbedded]);

  if (isFullPage) {
    return (
      <Box width="100%" height="100%" bg="white" overflow="hidden">
        <AdminWrapper>
          <AdminComponent config={config} />
        </AdminWrapper>
      </Box>
    );
  }

  return (
    <WidgetModalProvider elementId={config.elementId}>
      <>
        {/* Floating button */}
        {!isOpen && !isEmbedded && (
          <Box
            position="fixed"
            bottom="30px"
            right={config.position === 'left' ? 'auto' : '30px'}
            left={config.position === 'left' ? '30px' : 'auto'}
            zIndex={9999}
            bg={config.theme?.primaryColor || '#181e26'}
            borderRadius="16px"
            onClick={() => setIsOpen(true)}
            cursor="pointer"
            width="64px"
            height="64px"
            boxShadow="0 8px 32px rgba(0,0,0,0.18)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{
              filter: 'brightness(1.1)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
            }}
            transition="all 0.2s"
          >
            {/* Chat bubble icon */}
            <Box
              as="span"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {/* Chat bubble shape */}
              <Box
                position="relative"
                width="32px"
                height="24px"
                border={`3px solid ${config.theme?.secondaryColor || 'white'}`}
                borderRadius="50px 50px 50px 8px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _before={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: '2px',
                  width: '8px',
                  height: '8px',
                  bg: config.theme?.secondaryColor || 'white',
                  borderRadius: '50%',
                }}
              >
                {/* Three dots inside the bubble */}
                <Box display="flex" gap="2px" mt="-2px">
                  <Box
                    width="3px"
                    height="3px"
                    bg={config.theme?.secondaryColor || 'white'}
                    borderRadius="50%"
                  />
                  <Box
                    width="3px"
                    height="3px"
                    bg={config.theme?.secondaryColor || 'white'}
                    borderRadius="50%"
                  />
                  <Box
                    width="3px"
                    height="3px"
                    bg={config.theme?.secondaryColor || 'white'}
                    borderRadius="50%"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Backdrop overlay */}
        {isOpen && !isEmbedded && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            zIndex={100}
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Container: modal when not embedded; inline when embedded */}
        {isOpen && (
          <Box
            position={isEmbedded ? 'relative' : 'fixed'}
            top={isEmbedded ? undefined : '50%'}
            left={isEmbedded ? undefined : '50%'}
            transform={isEmbedded ? undefined : 'translate(-50%, -50%)'}
            width={isEmbedded ? containerWidth : '1200px'}
            height={isEmbedded ? containerHeight : '700px'}
            maxWidth={isEmbedded ? '100%' : '1200px'}
            minWidth={isEmbedded ? containerWidth : '1200px'}
            minHeight={isEmbedded ? containerHeight : '700px'}
            maxHeight={isEmbedded ? undefined : undefined}
            bg="white"
            borderRadius={isEmbedded ? 'none' : '2xl'}
            boxShadow={isEmbedded ? 'none' : '0 8px 32px rgba(0,0,0,0.22)'}
            overflow={isEmbedded ? 'visible' : 'hidden'}
            zIndex={isEmbedded ? 'auto' : 100}
            display="flex"
            flexDirection="column"
            css={{ overscrollBehavior: 'contain' }}
          >
            {/* Close button (modal mode only) */}
            {!isEmbedded && (
              <IconButton
                aria-label="Close chat"
                size="md"
                position="absolute"
                top={3}
                right={2}
                zIndex={999}
                onClick={() => setIsOpen(false)}
                variant="ghost"
                bg="rgba(255, 255, 255, 0.9)"
                borderRadius="full"
                boxShadow="md"
                _hover={{ bg: 'rgba(0, 0, 0, 0.1)' }}
                backdropFilter="blur(4px)"
              >
                <FiX />
              </IconButton>
            )}

            <Box
              width="100%"
              height={isEmbedded ? 'auto' : '100%'}
              maxHeight={isEmbedded ? undefined : '100%'}
              overflowY="auto"
              css={{
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
              onWheel={isEmbedded ? (e) => e.stopPropagation() : undefined}
              onTouchMove={isEmbedded ? (e) => e.stopPropagation() : undefined}
            >
              <AdminWrapper>
                <AdminComponent config={config} />
              </AdminWrapper>
            </Box>
          </Box>
        )}
      </>
    </WidgetModalProvider>
  );
}

const AdminWrapper = ({ children }: { children: React.ReactNode }) => (
  <CoreAdminContext
    dataProvider={dataProvider}
    authProvider={authProvider}
    queryClient={queryClient}
  >
    {children}
  </CoreAdminContext>
);
const AdminComponent = ({ config }: { config: WidgetConfig }) => {
  const navigate = useNavigate();
  const { data: user, error } = useAuthUser({
    enabled: !!config.access_token,
  });

  useEffect(() => {
    if (user) {
      navigate('/inbox');
    }
  }, [user]);

  useEffect(() => {
    // @ts-expect-error TODO: fix this
    if (error?.status === 401) {
      authService.refreshToken();
    }
  }, [error]);

  return (
    <OrgLocationProvider
      defaultOrganisation={{
        id: config.organisation_id || '',
        name: 'Organization',
      }}
      defaultLocation={{
        id: config.location_id || '',
        name: 'Location',
      }}
    >
      <CoreAdminUI
        layout={WidgetLayout}
        // TODO: Uncomment this when we have a login page
        loginPage={LoginPage}
        requireAuth
        disableTelemetry
        title="Conversations"
      >
        <CustomRoutes>
          <Route
            path="/organisation-selection"
            element={<OrganisationSelectionPage />}
          />
          <Route
            path="/location-selection"
            element={<LocationSelectionPage />}
          />

          <Route
            path="/inbox"
            element={
              <WhatsAppEventsProvider
                wsUrl={`${env.VITE_WHATSAPP_SERVER_WEBSOCKET_URL}`}
                reconnectMaxAttempts={10}
                initialReconnectDelay={1000}
                maxReconnectDelay={30000}
              >
                <ConversationsProvider
                  autoSelectFirstConversation={config.userPhone ? false : true}
                >
                  <ConversationsPage
                    hideConversationList={config.hideConversationList}
                    hideHeader={config.hideHeader}
                    userPhone={config.userPhone}
                    userFirstName={config.userFirstName}
                    userLastName={config.userLastName}
                    userMember={config.userMember}
                  />
                </ConversationsProvider>
              </WhatsAppEventsProvider>
            }
          />

          <Route path="/" element={<Navigate to="/inbox" replace />} />
          <Route path="*" element={<Navigate to="/inbox" replace />} />
        </CustomRoutes>
      </CoreAdminUI>
    </OrgLocationProvider>
  );
};

export default ConversationWidget;
